import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { GridSearch } from '../../components/GridSearch';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { confirmDiscardIfUnsaved, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import './AllParamsPopup.css';

// ─────────────────────────────────────────────────────────────────────────────
// All Param Results popup — ALPACA parity (ReportingViewController.cs).
//
//   Open  : AllParamResults_Execute (.cs:6909) — since 2026-10-30 the analytes of the
//           (sample, test) groups ticked in the detail grid (G5: every test of the sample),
//           at Pending Reporting or later; Save keeps one analyte per group.
//   Edit  : each analyte has a checkbox (checked = reportable).
//   Save  : Dc_Accepting (.cs:4578) — persists NotParamReport = !checked per
//           SampleParameter (PUT .../notparamreport).  Unchecked analytes are
//           then excluded from the rendered report (the render's not-report
//           filter drops NotParamReport=1).
//
// Owner 2026-10-30: built on the shared AppDialog + AppDataGrid (padding, sort,
// Excel column filters, pagination, row count in the footer), and it opens with
// every analyte TICKED except the ones already excluded (NotParamReport = 1), so
// the user only unticks what to leave out. (G5 opened with nothing ticked.)
// ─────────────────────────────────────────────────────────────────────────────

export interface AllParamRow {
  parameterOid:  string;
  jobId:         string | null;
  sampleId:      string | null;
  clientSample:  string | null;
  matrix:        string | null;
  test:          string | null;
  method:        string | null;
  parameter:     string | null;
  numericResult: string | null;
  result:        string | null;
  finalResult:   string | null;
  status:        string | null;
  notParamReport: boolean;
}

/** Audit / reason-gate name: the popup edits Custom Reporting data. */
const FORM_NAME = 'Custom Reporting';

// ALPACA All Param Results columns (per the 2026-06-07 user reorder):
//   JobID | SampleID | ClientSampleID | Matrix | Test | Method | Parameter |
//   NumericResult | Result | Status
const COLUMNS: AppColumn<AllParamRow>[] = [
  { field: 'jobId',         header: 'JobID',          width: 100 },
  { field: 'sampleId',      header: 'SampleID',       width: 120 },
  { field: 'clientSample',  header: 'ClientSampleID', width: 150 },
  { field: 'matrix',        header: 'Matrix',         width:  80 },
  { field: 'test',          header: 'Test',           width: 140 },
  { field: 'method',        header: 'Method',         width: 100 },
  { field: 'parameter',     header: 'Parameter',      width: 150 },
  { field: 'numericResult', header: 'NumericResult',  width: 120, align: 'right' },
  { field: 'result',        header: 'Result',         width: 110 },
  { field: 'status',        header: 'Status',         width: 150, type: 'status' },
];

/** The analytes whose stored state is "reported" — the ones ticked on open. */
export function initiallyChecked(rows: AllParamRow[]): Set<string | number> {
  return new Set(rows.filter(r => !r.notParamReport).map(r => r.parameterOid));
}

/**
 * The first (sample, test) group with no analyte ticked, as "SampleID · Test", or
 * null when every group keeps at least one (owner rule 2026-10-30; the save
 * endpoint enforces the same rule).
 */
export function groupWithNothingChecked(rows: AllParamRow[], checked: Set<string | number>): string | null {
  const groups = new Map<string, { label: string; any: boolean }>();
  for (const r of rows) {
    const key = `${r.sampleId ?? ''}|${r.test ?? ''}|${r.method ?? ''}`;
    const g = groups.get(key) ?? { label: `${r.sampleId ?? ''} · ${r.test ?? ''}`, any: false };
    if (checked.has(r.parameterOid)) g.any = true;
    groups.set(key, g);
  }
  for (const g of groups.values()) if (!g.any) return g.label;
  return null;
}

/** Rows matching the search text in any column. */
function filterRows(rows: AllParamRow[], search: string): AllParamRow[] {
  const t = search.trim().toLowerCase();
  if (!t) return rows;
  return rows.filter(r =>
    COLUMNS.some(c => String(r[c.field as keyof AllParamRow] ?? '').toLowerCase().includes(t)));
}

interface Props {
  title:         string;
  /** POST endpoint returning the analyte rows (body = selected ParameterOids). */
  fetchEndpoint: string;
  /** The selected ParameterOids sent as the POST body. */
  fetchBody:     string[];
  /** PUT endpoint persisting [{ parameterOid, notParamReport }]. */
  saveEndpoint:  string;
  onClose:       () => void;
  /** Called after a successful save (e.g. to refresh the detail grid). */
  onSaved?:      () => void;
}

export function AllParamsPopup({ title, fetchEndpoint, fetchBody, saveEndpoint, onClose, onSaved }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const [rows,    setRows]    = useState<AllParamRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState<string | null>(null);
  const [search,  setSearch]  = useState('');
  // checked = the analyte is SELECTED TO REPORT (NotParamReport will be set false).
  const [checked, setChecked] = useState<Set<string | number>>(new Set());

  // ── Load the analyte rows for the ticked samples ────────────────────────
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    api.post(fetchEndpoint, fetchBody ?? [])
      .then(res => {
        if (!alive) return;
        const data = (res.data as { data?: AllParamRow[] }).data ?? [];
        setRows(data);
        setChecked(initiallyChecked(data));
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setErr((e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message ?? 'Failed to load.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // fetchBody is stringified for a stable dep-check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEndpoint, JSON.stringify(fetchBody)]);

  const visible = useMemo(() => filterRows(rows, search), [rows, search]);

  // The analytes whose NotParamReport (= !checked) differs from what is stored.
  const updates = useMemo(
    () => rows
      .filter(r => (!checked.has(r.parameterOid)) !== r.notParamReport)
      .map(r => ({ parameterOid: r.parameterOid, notParamReport: !checked.has(r.parameterOid) })),
    [rows, checked]);
  const dirty = updates.length > 0;
  // The ticks live in grid checkboxes the automatic tracker ignores (standing rule 15).
  useUnsavedGuard(dirty);

  // ✕ / Cancel / Esc / backdrop: ask first when ticks have changed.
  const close = () => { void confirmDiscardIfUnsaved().then(leave => { if (leave) onClose(); }); };

  const toggle = (id: string) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const save = () => {
    if (!dirty) { onClose(); return; }   // nothing changed — Save just closes
    const empty = groupWithNothingChecked(rows, checked);
    if (empty) {
      notify.warning(`At least one parameter must stay selected for each sample test (${empty}).`);
      return;
    }
    void runSave({
      formName: FORM_NAME,
      auditAction: 'edit',
      save: async () => { await api.put(saveEndpoint, updates); },
      onSaved: () => { onSaved?.(); onClose(); },
      onError: (e: unknown) => {
        notify.error((e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message ?? 'Save failed.');
      },
    });
  };

  return (
    <AppDialog
      open
      title={title}
      width={1180}
      className="dlg-center"
      maximizable
      movable
      onClose={close}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={loading} onClick={save}
                  title="Save — ticked analytes report; unticked analytes are excluded from the report">
            <i className="ti ti-device-floppy" /> Save
          </button>
          <button type="button" className="ca-btn" onClick={close}>Cancel</button>
        </>
      }
    >
      <div className="app-top">
        <span className="app-count">
          {checked.size} of {rows.length} Checked to Report · untick the analytes to leave out
        </span>
        {/* Enter here searches — it must not press Save (AppDialog's Enter-to-submit). */}
        <div data-hotkeys-ignore>
          <GridSearch value={search} onChange={setSearch} placeholder="Search…" />
        </div>
      </div>
      {err && <div className="app-err">{err}</div>}
      <AppDataGrid<AllParamRow>
        rows={visible}
        columns={COLUMNS}
        getRowId={r => r.parameterOid}
        loading={loading}
        defaultPageSize={20}
        selectable
        selectedIds={checked}
        onSelectionChange={setChecked}
        onRowClick={r => toggle(r.parameterOid)}
        exportFilename="All-Param-Results"
        emptyText="No analytes for the selected sample(s)."
      />
    </AppDialog>
  );
}
